import { gql, useLazyQuery } from '@apollo/client'
import * as chrono from 'chrono-node'
import cx from 'classnames'
import PageComponent from 'components/Dashboard/PageComponent'
import { Form, Formik } from 'formik'
import { toFriendlyKitId } from 'helpers/string'
import { useEffect } from 'react'
import { StringParam, useQueryParam } from 'use-query-params'
import DateInput from '../../components/DateInput'
import SubmitButton from '../../components/SubmitButton'
import TextInput from '../../components/TextInput'
import { DashboardTitle } from './DashboardTitleContext'
import KitDetail, { kitDetailFragment } from './KitDetail'
import { KitById, KitByIdVariables } from './__generated__/KitById'

export const KIT_BY_ID_QUERY = gql`
  query KitById($id: ID!) {
    kitById(id: $id) {
      ...KitDetailFragment
    }
  }

  ${kitDetailFragment}
`

const FormError = ({ children }) => (
  <article className="message is-danger">
    <div className="message-body">{children}</div>
  </article>
)

const KitIdSearch = () => {
  const [search, { called, loading, data, error }] = useLazyQuery<KitById, KitByIdVariables>(KIT_BY_ID_QUERY, {
    fetchPolicy: 'cache-and-network',
  })

  const [kitId] = useQueryParam('kitId', StringParam)

  useEffect(() => {
    if (kitId) {
      search({ variables: { id: kitId } })
    }
  }, [kitId, search])
  return (
    <>
      <Formik
        initialValues={{ kitId }}
        onSubmit={async (values) => {
          await search({ variables: { id: values.kitId } })
        }}
      >
        {({ isSubmitting }) => (
          <Form>
            <div className="columns">
              <div className="column is-half">
                <TextInput
                  name="kitId"
                  placeholder="Kit ID to find"
                  addons={
                    <SubmitButton
                      className={cx('is-primary', {
                        'is-loading': isSubmitting,
                      })}
                    >
                      Search
                    </SubmitButton>
                  }
                />
              </div>
            </div>
          </Form>
        )}
      </Formik>

      {loading && 'Loading...'}
      {error && <FormError>{error.message}</FormError>}
      {data?.kitById && (
        <>
          <DashboardTitle title={`K${toFriendlyKitId(data.kitById.id.replace('Kit-', ''))}`} />
          <KitDetail kit={data.kitById} />
        </>
      )}
      {called && !loading && !data?.kitById && 'Not found'}
    </>
  )
}

const KIT_SEARCH_QUERY = gql`
  query KitSearch($dob: String) {
    kitSearch(input: { dob: $dob }) {
      id
      createdAt
      status
      registrationInfo {
        firstName
        lastName
        dob
        email
      }
    }
  }
`

const KitSearch = () => {
  const [search, { loading, data, error }] = useLazyQuery(KIT_SEARCH_QUERY, {
    fetchPolicy: 'no-cache',
  })

  return (
    <>
      <Formik
        initialValues={{ dob: '' }}
        onSubmit={async (values) => {
          await search({
            variables: {
              dob: chrono.en.GB.parseDate(values.dob, new Date(2004, 2, 22)).toISOString().substring(0, 10),
            },
          })
        }}
      >
        <Form>
          <DateInput name="dob" />
          <SubmitButton>Search</SubmitButton>
        </Form>
      </Formik>

      {loading && 'Loading...'}
      {error && <FormError>{error.message}</FormError>}
      {data && (
        <table className="table is-bordered">
          <thead>
            <tr>
              <th>ID</th>
              <th>Status</th>
              <th>First Name</th>
              <th>Last Name</th>
              <th>Email</th>
            </tr>
          </thead>
          <tbody>
            {data.kitSearch.map((kit) => (
              <tr key={kit.id}>
                <td>{kit.id}</td>
                <td>{kit.status}</td>
                <td>{kit.registrationInfo?.firstName}</td>
                <td>{kit.registrationInfo?.lastName}</td>
                <td>{kit.registrationInfo?.email}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  )
}

const Kits: PageComponent = () => (
  <div className="container">
    <DashboardTitle title="Kits" />

    <KitIdSearch />

    <h2 className="subtitle is-4">Search for kits</h2>
    <KitSearch />
  </div>
)

Kits.path = '/kits'
Kits.displayName = 'Kits'

export default Kits
